import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ChevronLeft,
  Loader2,
  RotateCcw,
  Search as SearchIcon,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import AdminLayout from "./AdminLayout";
import { Card, StatusPill, fmtDateTime } from "./_shared";
import { Section, Field, Row } from "./assessment/sections";
import { deepGet, fmtBool, fmtMoney } from "./assessment/format";
import WorkHistoryCard from "./assessment/WorkHistoryCard";
import SkillsCard from "./assessment/SkillsCard";
import EducationCard from "./assessment/EducationCard";
import InterestsAndConstraints from "./assessment/InterestsAndConstraints";
import AiReportCard from "./assessment/AiReportCard";
import RawJsonCard from "./assessment/RawJsonCard";

export default function AdminAssessmentDetail() {
  const { userId } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [rerunningAI, setRerunningAI] = useState(false);
  const [rerunningJobs, setRerunningJobs] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/assessments/${userId}`);
      setData(data);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load.",
      );
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const rerunAI = async () => {
    setRerunningAI(true);
    try {
      await api.post(`/admin/assessments/${userId}/rerun-ai`);
      toast.success("AI re-analysis complete");
      await refresh();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to rerun AI.",
      );
    } finally {
      setRerunningAI(false);
    }
  };

  const rerunJobs = async () => {
    setRerunningJobs(true);
    try {
      const { data: r } = await api.post(`/admin/users/${userId}/rerun-jobs`);
      toast.success(
        `Job search complete (${r.match?.jobs?.length || 0} roles)`,
      );
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to rerun jobs.",
      );
    } finally {
      setRerunningJobs(false);
    }
  };

  if (loading || !data) {
    return (
      <AdminLayout title="Assessment" subtitle={userId}>
        <div
          className="flex items-center gap-2 text-slate-500 text-sm"
          data-testid="admin-assessment-loading"
        >
          <Loader2 className="h-4 w-4 animate-spin" /> Loading...
        </div>
      </AdminLayout>
    );
  }

  const a = data.assessment || {};
  const raw = data.raw || {};
  // Prefer canonical; fall back to raw to surface legacy seed shapes.
  const pick = (path) => deepGet(a, path) ?? deepGet(raw, path);
  const u = data.user;
  const ai = a.ai_recommendation || raw.ai_recommendation || {};
  const workHistory = pick("work_history") || [];
  const skills = pick("skills") || [];
  const education = pick("education") || [];
  const hobbies = pick("hobbies") || raw.hobbies || [];
  const constraints = pick("physical_constraints") || {};

  return (
    <AdminLayout
      title={u?.name || u?.email || "Assessment"}
      subtitle={u?.email}
      actions={
        <div className="flex items-center gap-2">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/archive/responses/${userId}.pdf`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
            data-testid="admin-assessment-responses-pdf-button"
            title="Download a PDF transcript of this candidate's questionnaire responses"
          >
            <FileText className="h-3.5 w-3.5" /> Responses PDF
          </a>
          <Link
            to={`/admin/users/${userId}`}
            className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
            data-testid="admin-assessment-open-user"
          >
            Open user
          </Link>
          <button
            type="button"
            onClick={rerunAI}
            disabled={rerunningAI}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
            data-testid="admin-assessment-rerun-ai-button"
          >
            <RotateCcw
              className={`h-4 w-4 ${rerunningAI ? "animate-spin" : ""}`}
            />
            {rerunningAI ? "Running..." : "Rerun AI"}
          </button>
          <button
            type="button"
            onClick={rerunJobs}
            disabled={rerunningJobs}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            data-testid="admin-assessment-rerun-jobs-button"
          >
            <SearchIcon
              className={`h-4 w-4 ${rerunningJobs ? "animate-spin" : ""}`}
            />
            {rerunningJobs ? "Searching..." : "Rerun jobs"}
          </button>
          <Link
            to="/admin/assessments"
            className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            data-testid="admin-assessment-back-link"
          >
            <ChevronLeft className="h-4 w-4" /> Back
          </Link>
        </div>
      }
    >
      <div
        className="grid grid-cols-1 lg:grid-cols-3 gap-6"
        data-testid="admin-assessment-detail"
      >
        {/* Status / meta */}
        <Card className="p-5 lg:col-span-1" testid="admin-assessment-summary">
          <h2 className="text-sm font-medium text-slate-900 mb-3">Status</h2>
          <dl className="text-sm space-y-2">
            <Row label="Status">
              <StatusPill
                value={a.status || raw.status || "—"}
                tone={
                  (a.status || raw.status) === "processed"
                    ? "green"
                    : (a.status || raw.status) === "submitted"
                      ? "sky"
                      : "slate"
                }
              />
            </Row>
            <Row label="Submitted">
              {pick("submitted_at") ? fmtDateTime(pick("submitted_at")) : "—"}
            </Row>
            <Row label="Updated">{fmtDateTime(pick("updated_at"))}</Row>
            <Row label="Created">{fmtDateTime(pick("created_at"))}</Row>
            <Row label="Assessment ID">
              <span className="font-mono text-xs break-all">
                {pick("assessment_id") || pick("_id") || "—"}
              </span>
            </Row>
            <Row label="User ID">
              <span className="font-mono text-xs break-all">{userId}</span>
            </Row>
          </dl>
        </Card>

        <Section title="Current situation" testid="sect-current">
          <Field
            label="Role title"
            value={
              pick("current_situation.role_title") ||
              pick("current_situation.last_role")
            }
          />
          <Field
            label="Industry"
            value={
              pick("current_situation.industry") ||
              pick("current_situation.last_industry")
            }
          />
          <Field
            label="Employment status"
            value={pick("current_situation.employment_status")}
          />
          <Field
            label="Months since displacement"
            value={pick("current_situation.months_since_displacement")}
          />
          <Field
            label="Displacement reason"
            value={pick("current_situation.displacement_reason")}
          />
          <Field
            label="Description"
            value={pick("current_situation.description")}
            multiline
          />
        </Section>

        <Section title="Career goals" testid="sect-goals">
          <Field
            label="Primary goal"
            value={pick("career_goals.primary_goal")}
            multiline
          />
          <Field
            label="Willing to relocate"
            value={fmtBool(pick("career_goals.willing_to_relocate"))}
          />
          <Field
            label="Preferred work mode"
            value={pick("career_goals.preferred_work_mode")}
          />
          <Field
            label="Target salary (AUD)"
            value={fmtMoney(pick("career_goals.target_salary_aud"))}
          />
        </Section>

        <Section title="Location" testid="sect-location" full>
          <Field
            label="City"
            value={
              pick("location.city") || pick("location_travel_relocation.city")
            }
          />
          <Field
            label="Region"
            value={
              pick("location.region") ||
              pick("location_travel_relocation.region")
            }
          />
          <Field
            label="Country"
            value={
              pick("location.country") ||
              pick("location_travel_relocation.country")
            }
          />
          <Field
            label="Open to relocation"
            value={fmtBool(pick("location_travel_relocation.open_to_relocation"))}
          />
          <Field
            label="Open to remote"
            value={fmtBool(pick("location_travel_relocation.open_to_remote"))}
          />
        </Section>

        <Section title="Financial position" testid="sect-finance">
          <Field
            label="Current salary (AUD)"
            value={fmtMoney(pick("salary.current_aud"))}
          />
          <Field
            label="Target salary (AUD)"
            value={fmtMoney(pick("salary.target_aud"))}
          />
          <Field
            label="Runway (months)"
            value={pick("financial_position.runway_months")}
          />
          <Field
            label="Dependants"
            value={pick("financial_position.dependants")}
          />
        </Section>

        <Section title="Retraining & personality" testid="sect-retrain">
          <Field
            label="Willing to retrain"
            value={fmtBool(pick("retraining_capacity.willing"))}
          />
          <Field
            label="Hours / week"
            value={pick("retraining_capacity.weekly_hours")}
          />
          <Field label="Work style" value={pick("personality.work_style")} />
          <Field label="Energy" value={pick("personality.energy")} />
        </Section>

        <WorkHistoryCard entries={workHistory} />
        <SkillsCard skills={skills} />
        <EducationCard entries={education} />
        <InterestsAndConstraints hobbies={hobbies} constraints={constraints} />
        <AiReportCard ai={ai} />
        <RawJsonCard raw={raw} />
      </div>
    </AdminLayout>
  );
}
